import { Phone, Truck, CheckCircle, Camera, Wrench, MapPin } from "@phosphor-icons/react";

/**
 * "Watch a Crew Roll Out" — 3-panel storyboard section for the landing page.
 * Shows the crew's morning: ping → truck → done. All fabricated demo data.
 */
export default function CrewRolloutStoryboard() {
  return (
    <section className="py-16 sm:py-20 bg-slate-50" data-testid="landing-storyboard">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="text-xs font-black uppercase tracking-widest mb-3 text-teal-600">A Morning on Field Radar</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight text-[#0b1220]">
            Watch a crew roll out
          </h2>
          <p className="mt-3 text-sm text-slate-600">Three taps between the WO landing and a photo-verified close.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {/* Panel 1: 6:42 AM ping */}
          <Panel time="6:42 AM" accent="bg-red-500" title="WO pings the on-call tech"
            body="Voice call from tenant lands. AI Voice Reception logs the request and dispatches to the closest available tech.">
            <div className="bg-[#0b1220] text-white p-3 border-2 border-[#0b1220]">
              <div className="flex items-center gap-2">
                <Phone weight="fill" size={16} className="text-red-400 animate-pulse"/>
                <div className="text-[9px] font-black uppercase tracking-widest text-red-300">Incoming · line 1</div>
              </div>
              <div className="mt-2 text-sm italic text-slate-300">"Hey, my kitchen sink is dripping again..."</div>
              <div className="mt-3 p-2 bg-white/5 border border-slate-700">
                <div className="text-[9px] font-black uppercase tracking-widest text-teal-300">AI drafted WO</div>
                <div className="text-xs font-black mt-0.5">Kitchen sink leak · 742 Elm St · Apt 2B</div>
                <div className="text-[10px] text-slate-400 mt-0.5">↳ Assigned to Myke · ETA 25 min</div>
              </div>
            </div>
          </Panel>

          {/* Panel 2: 6:58 AM Show-Up Card in truck */}
          <Panel time="6:58 AM" accent="bg-amber-500" title="Show-Up Card in the truck"
            body="Tech opens the WO on their phone. One screen: navigate, door codes, tenant tap-to-call, and 3 nearby WOs to bundle.">
            <div className="bg-white border-2 border-[#0b1220] p-3">
              <div className="flex items-start gap-2">
                <MapPin weight="fill" size={14} className="text-cyan-600 mt-0.5"/>
                <div>
                  <div className="font-display font-black text-sm leading-tight">742 Elm St · Apt 2B</div>
                  <div className="mt-1 flex gap-1 flex-wrap">
                    <span className="px-1.5 py-0.5 bg-emerald-500 text-white font-black uppercase text-[8px] tracking-widest">✓ ENTER</span>
                    <span className="px-1.5 py-0.5 bg-fuchsia-500 text-white font-black uppercase text-[8px] tracking-widest">🎯 3 MORE HERE</span>
                  </div>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1">
                <CodeMini label="Building" value="7-4-2-1"/>
                <CodeMini label="Unit Key" value="384029"/>
              </div>
              <div className="mt-2 p-1.5 bg-red-50 border border-red-300 text-[10px] italic text-red-800">
                🚨 Callback from WO#4681 — bring pressure gauge
              </div>
            </div>
          </Panel>

          {/* Panel 3: 7:20 AM done with photos */}
          <Panel time="7:20 AM" accent="bg-emerald-500" title="Closed · photo-verified"
            body="Voice-note transcribed → tenant + office texted automatically. Photo gate confirms both before + after are on file.">
            <div className="bg-white border-2 border-[#0b1220] p-3">
              <div className="flex items-center gap-2">
                <CheckCircle weight="fill" size={16} className="text-emerald-500"/>
                <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700">WO CLOSED · 38 min total</div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1">
                <PhotoBox tag="BEFORE" tone="bg-slate-200"/>
                <PhotoBox tag="AFTER"  tone="bg-emerald-100"/>
              </div>
              <div className="mt-2 p-1.5 bg-emerald-50 border border-emerald-300 text-[10px] italic text-emerald-800">
                "P-trap replaced, tightened supply valve. Also caught a corroded shutoff — flagged for follow-up."
              </div>
              <div className="mt-1 text-[9px] font-black uppercase tracking-widest text-slate-500">
                ↳ Tenant + owner + AppFolio all synced
              </div>
            </div>
          </Panel>
        </div>

        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0b1220] text-white font-black uppercase text-xs tracking-widest">
            <Wrench weight="fill" size={14} className="text-teal-300"/> Total human typing: 0 characters
          </div>
        </div>
      </div>
    </section>
  );
}

function Panel({ time, accent, title, body, children }) {
  return (
    <div className="bg-white border-2 border-[#0b1220] p-5 shadow-[6px_6px_0_#0b1220] flex flex-col">
      <div className={`inline-flex items-center gap-2 self-start px-2 py-1 ${accent} text-white font-mono font-black text-xs`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>{time}
      </div>
      <h3 className="mt-3 font-display font-black text-lg tracking-tight text-[#0b1220]">{title}</h3>
      <p className="mt-1 text-xs text-slate-600 leading-relaxed">{body}</p>
      <div className="mt-4">{children}</div>
    </div>
  );
}

const CodeMini = ({ label, value }) => (
  <div className="p-1 bg-cyan-50 border border-cyan-400">
    <div className="text-[8px] font-black uppercase tracking-widest text-cyan-800">{label}</div>
    <div className="font-mono font-black text-xs">{value}</div>
  </div>
);

const PhotoBox = ({ tag, tone }) => (
  <div className={`${tone} h-16 relative flex items-end p-1 border border-slate-300`}>
    <Camera weight="bold" size={20} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-slate-400 opacity-40"/>
    <span className="text-[8px] font-black uppercase tracking-widest bg-white/80 px-1 relative">{tag}</span>
  </div>
);
